import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeContextValue = {
  isDark: boolean;
  toggleTheme: () => void;
  setDark: (val: boolean) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const LS_KEY = 'IsDarkModeActivated';

function applyTheme(isDark: boolean) {
  try {
    // Remove previously injected custom theme css (light/dark custom file)
    const existing = document.querySelector('link[data-theme]');
    if (existing?.parentNode) existing.parentNode.removeChild(existing);

    const cssFile = isDark ? '/assets/css/darkmode.css' : '/assets/css/lightmode.css';
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssFile;
    link.setAttribute('data-theme', 'theme-css');
    document.head.appendChild(link);

    const themeLink = document.getElementById('theme-link') as HTMLLinkElement | null;
    if (themeLink) {
      themeLink.href = isDark
        ? '/primereact/resources/themes/lara-dark-cyan/theme.css'
        : '/primereact/resources/themes/lara-light-cyan/theme.css';
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed applying theme', e);
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  
  // Check if running in iframe (embedding mode)
  const isEmbedded = (() => {
    try {
      return window.self !== window.top;
    } catch {
      return true; // Cross-origin error means we ARE in an iframe
    }
  })();
  
  // In embedding mode, prefer URL theme param over localStorage
  const getInitialTheme = () => {
    if (isEmbedded) {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const themeParam = searchParams.get('theme');
        if (themeParam === 'dark') return true;
        if (themeParam === 'light') return false;
      } catch {}
    }
    const ls = localStorage.getItem(LS_KEY);
    return ls ? ls === 'true' : systemPrefersDark;
  };
  
  const [isDark, setIsDark] = useState<boolean>(getInitialTheme);

  // Apply theme whenever value changes
  useEffect(() => {
    applyTheme(isDark);
    // Only persist to localStorage when NOT embedded (parent controls theme in iframe)
    if (!isEmbedded) {
      try {
        localStorage.setItem(LS_KEY, isDark.toString());
      } catch {}
    }
  }, [isDark, isEmbedded]);

  // Listen to storage changes across tabs (only when not embedded)
  useEffect(() => {
    if (isEmbedded) return; // Parent controls theme in iframe
    const onStorage = (e: StorageEvent) => {
      if (e.key === LS_KEY && e.newValue != null) {
        setIsDark(e.newValue === 'true');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isEmbedded]);

  // Listen to system preference changes when user hasn't manually chosen yet (only when not embedded)
  useEffect(() => {
    if (isEmbedded) return; // Parent controls theme in iframe
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (ev: MediaQueryListEvent) => {
      const ls = localStorage.getItem(LS_KEY);
      // Only auto-follow system if user hasn't explicitly toggled (i.e. LS absent)
      if (!ls) {
        setIsDark(ev.matches);
      }
    };
    mq.addEventListener('change', listener);
    return () => mq.removeEventListener('change', listener);
  }, [isEmbedded]);

  const toggleTheme = useCallback(() => setIsDark(prev => !prev), []);
  const setDark = useCallback((val: boolean) => setIsDark(val), []);

  const value: ThemeContextValue = useMemo(() => ({ isDark, toggleTheme, setDark }), [isDark, toggleTheme, setDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
